import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';

import {
    ENDPOINT_DATA_GEOSERVICIOS,
    GIS_CLUSTERING_ZOOM_THRESHOLD,
    GIS_LIGHTNING_ICON_RADIUS_PIXELS,
    //REALTIME_INTERVAL_SECONDS,
    REALTIME_INTERVAL_SECONDS_U,
} from '../../../constants';
import { dataGeoServiciosFetch } from '../../../helpers/fetch';
import { invertCoordinates } from '../../../helpers/generalFunctions';
import LightningsIcon from '../icons/LightningsIcon';
import LightningTooltip from '../tooltips/LightningTooltip';

import 'leaflet-realtime';
import 'leaflet.markercluster';

import 'leaflet.markercluster/dist/MarkerCluster.css';


/**
 * Get Light for the real time
 * @param {number} idCliente - Client id for the request.
 * @param {any:[]} colorGlm - Color of the lightings
 * @param {number} ptws - Client id for the request.
 * @param {string} request - Request of petition
 * @returns Array of two elements: realtime lightnings markerclustergroup and lightnings realtime reference, the
 * reference needs to be returned to stop the subscription later.
 */
const getLightningsMarkerClusterGroupRealtime = (idCliente,colorGlm,ptws,request='GLM') => {
    const lightningsMarkerClusterGroup = createLightningsMarkerClusterGroup();
    const realtimeReference = L.realtime(
        {
            url: ENDPOINT_DATA_GEOSERVICIOS,
            method: 'POST',
            body: JSON.stringify({
                solicitud: request,
                idCliente,
                ptws: ptws,
            }),
        },
        {
            container: lightningsMarkerClusterGroup,
            interval: REALTIME_INTERVAL_SECONDS_U * 1000,
            onEachFeature: (feature, layer) => {
                layer.bindTooltip(
                    ReactDOMServer.renderToString(
                        <LightningTooltip coordinates={feature.geometry.coordinates} {...feature.properties} />
                    ),
                    { direction: 'top' }
                );
            },
            pointToLayer: (feature, latlng) => {
                return L.circleMarker(latlng, {
                    fillColor: getLightningColor(feature.properties.difmin,colorGlm),
                    fillOpacity: 1,
                    pmIgnore: true,
                    radius: GIS_LIGHTNING_ICON_RADIUS_PIXELS,
                    stroke: false,
                });
            },
            removeMissing: true,
            updateFeature: (feature, oldLayer, newLayer) => newLayer,
        }
    );
    return [lightningsMarkerClusterGroup, realtimeReference];
};

/**
 * Get Light for the historic
 * @param {*} parameters - Parameters for the request.
 * @param {string} colorHistoric - Parameters colors.
 * @returns Historic lightnings markerclustergroup.
 */
const getLightningsMarkerClusterGroupHistoric = async (parameters,colorHistoric) => {
    const lightningsMarkerClusterGroup = createLightningsMarkerClusterGroup();
    const lightningsGeojson = await dataGeoServiciosFetch(parameters, 'POST');
    if (lightningsGeojson) {
        const lightningMarkers = lightningsGeojson.features.map((feature) =>
            createLightningMarker(
                feature,
                invertCoordinates(feature.geometry.coordinates),
                colorHistoric
            ).bindTooltip(
                ReactDOMServer.renderToString(
                    <LightningTooltip coordinates={feature.geometry.coordinates} {...feature.properties} />
                ),
                { direction: 'top' }
            )
        );
        lightningsMarkerClusterGroup.addLayers(lightningMarkers);
    }
    return lightningsMarkerClusterGroup;
};

/**
 * Create group of lights
 * @returns Lightnings markerclustergroup for realtime and historic, because the initialization is the same.
 */
const createLightningsMarkerClusterGroup = () => {
    return L.markerClusterGroup({
        chunkedLoading: true,
        disableClusteringAtZoom: GIS_CLUSTERING_ZOOM_THRESHOLD,
        iconCreateFunction: (cluster) =>
            L.divIcon({
                className: '',
                html: ReactDOMServer.renderToString(<LightningsIcon childCount={cluster.getChildCount()} />),
                iconSize: [35, 35],
            }),
        spiderfyOnMaxZoom: false,
    });
};

/**
 * Create circle
 * @param {*} feature - GeoJSON feature, the lightning varies depending on the feature properties.
 * @param {*} latlng - Latitude and longitude for the marker.
 * @returns Lightning marker (individual lightning). This marker is created as a circleMarker, which is a vector layer
 * and can be rendered in a <canvas> tag, improving performance.
 */
const   createLightningMarker = (feature, latlng,color) => {
    return L.circleMarker(latlng, {
        fillColor: getLightningColorHistoric(feature.properties.grupo,color),
        fillOpacity: 1,
        pmIgnore: true,
        radius: GIS_LIGHTNING_ICON_RADIUS_PIXELS,
        stroke: false,
    });
};

/**
 * Colors for the lights
 * @param {number} difmin - Property "difmin", the lightning color depends on this property.
 * @param {Array} color - Color selector for the points
 * @returns Lightning color, based on "difmin" property.
 */
const getLightningColor = (difmin,color) => {
    const colorSelector = color[0].split(',')

    if (difmin < 1) {
        return colorSelector[0];
    } else if (difmin < 5) {
        return colorSelector[1];
    } else if (difmin < 10) {
        return colorSelector[2];
    } else if (difmin < 20) {
        return colorSelector[3];
    } else if (difmin < 30) {
        return colorSelector[4];
    } else if (difmin < 40) {
        return colorSelector[5];
    } else if (difmin < 50) {
        return colorSelector[6];
    } else {
        return colorSelector[7];
    }
};

/**
 * Color fot the historic
 * @param {number} grupo - Property "grupo", the lightning color depends on this property.
 * @param {Array} color - Color selector for the points
 * @returns
 */
const getLightningColorHistoric = (grupo,color) => {
    const colorSelector = color[0].split(',')

    if (grupo === 1) {
        return colorSelector[7];
    } else if (grupo === 2) {
        return colorSelector[6];
    } else if (grupo === 3) {
        return colorSelector[5];
    } else if (grupo === 4) {
        return colorSelector[4];
    } else if (grupo === 5) {
        return colorSelector[3];
    } else if (grupo === 6) {
        return colorSelector[2];
    } else if (grupo === 7) {
        return colorSelector[1];
    } else if (grupo === 8) {
        return colorSelector[0];
    }
};

export { getLightningsMarkerClusterGroupHistoric, getLightningsMarkerClusterGroupRealtime };
